import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Form, Container, Row, Col, Button } from "react-bootstrap";
import PropertyList from "containers/property/PropertyList";
import { PacmanLoader } from "react-spinners";
import { get, post, put } from "aws-amplify/api";
import DatePicker from "react-datepicker";
import Select from "react-select";
import Moment from "moment";
import { createElementIdFactory } from "utils/elementIds";
import "containers/common/List.css";
import "containers/common/Table.css";

function serializeDate(value) {
  if (!value) return "";
  const momentValue = Moment(value);
  return momentValue.isValid() ? momentValue.local().format() : "";
}

function normalizeQueryValue(value) {
  if (value === "Invalid date" || value === null || value === undefined) {
    return "";
  }
  return value;
}

const initialFormData = {
  propertyKey: "",
  buildingName: "",
  address: "",
  address2: "",
  stnolow: "",
  stnohigh: "",
  description: "",
  zoning: "",
  historical: "",
  title: "",
  saleshistory: "",
  valuations: "",
  tenant: "",
  proposal: "",
  saleappendix: "",
  photorequired: "",
  activequeryfollowup: null,
  perpetual: "",
  saledateentered: null,
  salesheetfollowup: null,
  ownername: "",
  browselistidfilter: "",
  state: "",
  scope: "",
  hasleasingagent: "",
  missingphoto: "",
};

function PropertySearch({ isAuthenticated, userPermissions, user }) {
  const [historicalSearchOptions, setHistoricalSearchOptions] = useState([]);
  const [selectedHistoricalSearchOption, setSelectedHistoricalSearchOption] = useState(null);
  const [cityscopeOptions, setCityscopeOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [browseListsOptions, setBrowseListsOptions] = useState([]);
  const [selectedBrowseListOption, setSelectedBrowseListOption] = useState(null);
  const [browseListInputValue, setBrowseListInputValue] = useState("");
  const [selectedCityscopeOption, setSelectedCityscopeOption] = useState(null);
  const [selectedStateOption, setSelectedStateOption] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [browselistid, setBrowselistid] = useState(null);
  const [formData, setFormData] = useState(initialFormData);

  const createId = useMemo(() => createElementIdFactory("property-search"), []);
  const lookupFilter = useMemo(() => ({ user, screen: "property" }), [user]);

  const loadSearchLookups = useCallback(async () => {
    try {
      const [searchesResponse, cityscopesResponse, statesResponse, browseListsResponse] = await Promise.all([
        get({ apiName: "colin", path: "/searches", options: { queryParams: lookupFilter } }).response,
        get({ apiName: "colin", path: "/cityscopes/dropdown" }).response,
        get({ apiName: "colin", path: "/states" }).response,
        get({ apiName: "colin", path: "/browselists", options: { queryParams: lookupFilter } }).response,
      ]);

      const [searches, cityscopes, states, browseLists] = await Promise.all([
        searchesResponse.body.json(),
        cityscopesResponse.body.json(),
        statesResponse.body.json(),
        browseListsResponse.body.json(),
      ]);

      if (Array.isArray(searches)) {
        setHistoricalSearchOptions(searches);
        if (searches.length > 0) {
          setSelectedHistoricalSearchOption(searches[0]);
        }
      }
      if (Array.isArray(cityscopes)) {
        setCityscopeOptions(cityscopes);
      }
      if (Array.isArray(states)) {
        setStateOptions(states);
      }
      if (Array.isArray(browseLists)) {
        const formattedBrowseLists = browseLists.map((browseList) => ({
          value: browseList.id ?? browseList.uid ?? browseList.value,
          label: browseList.label ?? browseList.browse_list_name ?? browseList.name,
        }));
        setBrowseListsOptions(formattedBrowseLists);
      }
    } catch (e) {
      alert(e + ". Please refresh the page (F5) to try again.");
    }
  }, [lookupFilter]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    document.title = "Cityscope Property Search";
    loadSearchLookups();
  }, [isAuthenticated, loadSearchLookups]);

  function parseDate(value) {
    if (!value) return null;
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  function parseBoolean(value) {
    if (typeof value === "boolean") {
      return value;
    }
    if (typeof value === "number") {
      return value === 1;
    }
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["true", "1", "yes", "on"].includes(normalized)) {
        return true;
      }
      if (["false", "0", "no", "off", "", "null", "undefined"].includes(normalized)) {
        return false;
      }
      return false;
    }
    return false;
  }

  function getSearchParam(searchParams, keys, fallback = "") {
    if (!searchParams || typeof searchParams !== "object") {
      return fallback;
    }

    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(searchParams, key)) {
        return searchParams[key];
      }
    }

    const normalizedLookup = {};
    for (const existingKey of Object.keys(searchParams)) {
      normalizedLookup[existingKey.toLowerCase().replace(/_/g, "")] = searchParams[existingKey];
    }

    for (const key of keys) {
      const normalizedKey = key.toLowerCase().replace(/_/g, "");
      if (Object.prototype.hasOwnProperty.call(normalizedLookup, normalizedKey)) {
        return normalizedLookup[normalizedKey];
      }
    }

    return fallback;
  }

  const getProperties = useCallback(async (filter) => {
    const propertyFilter = {};
    for (const key in filter) {
      propertyFilter[key] = normalizeQueryValue(filter[key]);
    }

    const response = await get({
      apiName: "colin",
      path: "/properties",
      options: { queryParams: propertyFilter },
    }).response;

    try {
      const apiProperties = await response.body.json();
      return apiProperties;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
      return [];
    }
  }, []);

  const buildPropertyQuery = useCallback((searchData) => {
    const saleAppendixValue = searchData.saleappendix ?? "";

    return {
      propertykey: searchData.propertyKey,
      buildingname: searchData.buildingName,
      address: searchData.address,
      address2: searchData.address2,
      stnolow: searchData.stnolow,
      stnohigh: searchData.stnohigh,
      description: searchData.description,
      zoning: searchData.zoning,
      historical: searchData.historical,
      title: searchData.title,
      saleshistory: searchData.saleshistory,
      valuations: searchData.valuations,
      tenant: searchData.tenant,
      proposal: searchData.proposal,
      saleappendix: saleAppendixValue,
      photorequired: searchData.photorequired,
      activequeryfollowup: serializeDate(searchData.activequeryfollowup),
      perpetual: searchData.perpetual,
      saledateentered: serializeDate(searchData.saledateentered),
      salesheetfollowup: serializeDate(searchData.salesheetfollowup),
      ownername: searchData.ownername,
      browselistid: searchData.browselistidfilter,
      state: searchData.state,
      scope: searchData.scope,
      hasleasingagent: searchData.hasleasingagent,
      missingphoto: searchData.missingphoto,
      user,
      screen: "property",
    };
  }, [user]);

  const runSearch = useCallback(async (searchData) => {
    setIsLoading(true);
    try {
      const nextProperties = await getProperties(buildPropertyQuery(searchData));
      setProperties(Array.isArray(nextProperties) ? nextProperties : []);
    } finally {
      setIsLoading(false);
    }
  }, [getProperties, buildPropertyQuery]);

  const updateTableState = useCallback((updatedData, setName) => {
    if (setName === "properties") {
      setProperties(updatedData);
    }
  }, []);

  function handleChange(event) {
    const { id, name, value } = event.target;

    const deriveFieldFromId = (inputId) => {
      const idText = String(inputId || "");
      const match = idText.match(/input-(.+)$/);
      if (!match || !match[1]) {
        return "";
      }

      return match[1].replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
    };

    const fieldName = name || deriveFieldFromId(id);
    if (!fieldName) {
      return;
    }

    setFormData((previousFormData) => ({
      ...previousFormData,
      [fieldName]: value,
    }));
  }

  async function handleSubmit(event) {
    if (event) {
      event.preventDefault();
    }
    await runSearch(formData);
  }

  function clearFilters() {
    setFormData(initialFormData);
    setSelectedCityscopeOption(null);
    setSelectedStateOption(null);
    setSelectedHistoricalSearchOption(null);
  }

  function handleSearchChange(option) {
    setSelectedHistoricalSearchOption(option);

    if (!option || !option.search_parameters) {
      return;
    }

    const parsedSearchParameters = JSON.parse(option.search_parameters);
    const parsedPropertyKey = getSearchParam(parsedSearchParameters, ["propertyKey", "propertykey"], "");
    const parsedBuildingName = getSearchParam(parsedSearchParameters, ["buildingName", "buildingname"], "");
    const parsedSaleAppendix = getSearchParam(parsedSearchParameters, ["saleappendix"], getSearchParam(option, ["saleappendix"], ""));

    // Convert boolean search fields to empty string when false (UAT parity)
    const booleanSearchFields = ["photorequired", "perpetual", "hasleasingagent", "missingphoto"];
    const parsedBooleans = {};
    booleanSearchFields.forEach(field => {
      const parsed = parseBoolean(parsedSearchParameters[field]);
      parsedBooleans[field] = parsed ? true : "";
    });

    const nextFormData = {
      ...initialFormData,
      ...parsedSearchParameters,
      propertyKey: parsedPropertyKey,
      buildingName: parsedBuildingName,
      saleappendix: parsedSaleAppendix,
      activequeryfollowup: parseDate(parsedSearchParameters.activequeryfollowup),
      perpetual: parsedBooleans.perpetual,
      saledateentered: parseDate(parsedSearchParameters.saledateentered),
      salesheetfollowup: parseDate(parsedSearchParameters.salesheetfollowup),
      photorequired: parsedBooleans.photorequired,
      hasleasingagent: parsedBooleans.hasleasingagent,
      missingphoto: parsedBooleans.missingphoto,
    };

    setFormData(nextFormData);

    const nextState = parsedSearchParameters.state || null;
    const nextScope = parsedSearchParameters.scope || null;

    setSelectedStateOption(nextState ? { value: nextState, label: nextState } : null);
    setSelectedCityscopeOption(nextScope ? { value: nextScope, label: nextScope } : null);

    runSearch(nextFormData);
  }

  function handleStateChange(option) {
    if (option) {
      setSelectedStateOption(option);
      setFormData((previousFormData) => ({
        ...previousFormData,
        state: option.value,
      }));
    } else {
      setSelectedStateOption(null);
      setFormData((previousFormData) => ({
        ...previousFormData,
        state: "",
      }));
    }
  }

  function handleCityscopeChange(option) {
    if (option) {
      setSelectedCityscopeOption(option);
      setFormData((previousFormData) => ({
        ...previousFormData,
        scope: option.value,
      }));
    } else {
      setSelectedCityscopeOption(null);
      setFormData((previousFormData) => ({
        ...previousFormData,
        scope: "",
      }));
    }
  }

  function handleDateChange(date, name) {
    setFormData((prev) => ({
      ...prev,
      [name]: date && !Number.isNaN(date.getTime()) ? date : null,
    }));
  }

  function handleCheckBoxChange(event) {
    const { name, checked } = event.target;
    // For boolean search fields, use empty string when unchecked (not checked)
    // to match UAT behavior and ensure no false positives in search results
    const booleanSearchFields = ["photorequired", "perpetual", "hasleasingagent", "missingphoto"];
    const value = checked ? true : (booleanSearchFields.includes(name) ? "" : false);
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  const saveBrowseList = useCallback(async () => {
    if (!selectedBrowseListOption) {
      alert("Select a browse list on the left, or type a new browse list name to create a new browse list.");
      return;
    }

    const selectedProperties = properties.filter((property) => property.selected).map((property) => property.property_id || property.id || property.propertyid);
    if (!selectedProperties.length) {
      alert("Select at least one property before adding it to the browse list.");
      return;
    }

    try {
      const listName = selectedBrowseListOption.label;
      const isNewList = selectedBrowseListOption.value === "new" || browselistid === "new";

      const requestBody = {
        properties: selectedProperties,
        user,
      };

      const response = isNewList
        ? await post({
            apiName: "colin",
            path: "/browselists/new",
            options: {
              body: {
                browselist: {
                  user_name: user,
                  browse_list_name: listName,
                },
                properties: selectedProperties,
              },
            },
          }).response
        : await put({
            apiName: "colin",
            path: `/browselists/${selectedBrowseListOption.value}`,
            options: {
              body: requestBody,
            },
          }).response;

      const result = await response.body.json();

      if (result?.result === false) {
        alert(result.error_message || "Unable to save the browse list.");
        return;
      }

      if (isNewList && result?.id) {
        setBrowselistid(String(result.id));
        setSelectedBrowseListOption({ value: String(result.id), label: listName });
        await loadSearchLookups();
      }

      alert("Browse list updated");
    } catch (error) {
      alert(error?.message || "Unable to save the browse list.");
    }
  }, [selectedBrowseListOption, properties, browselistid, user, loadSearchLookups]);

  const handleBrowseListChange = useCallback((option) => {
    const nextOption = option || null;
    setSelectedBrowseListOption(nextOption);
    setBrowselistid(nextOption ? String(nextOption.value) : null);
    setBrowseListInputValue("");

    if (!nextOption) {
      setFormData((currentFormData) => ({
        ...currentFormData,
        browselistidfilter: "",
      }));
      return;
    }

    const nextFormData = {
      ...formData,
      browselistidfilter: nextOption.value === "new" ? "" : nextOption.value,
    };
    setFormData(nextFormData);

    if (nextOption.value === "new") {
      setProperties([]);
    }
  }, [formData]);

  const commitNewBrowseList = useCallback((inputValue) => {
    const listName = inputValue.trim();
    if (!listName) {
      return;
    }

    const nextOption = { value: "new", label: listName };
    setSelectedBrowseListOption(nextOption);
    setBrowselistid("new");
    setBrowseListInputValue("");
  }, []);

  const handleBrowseListInputChange = useCallback((inputValue, actionMeta) => {
    if (!actionMeta || actionMeta.action === "input-change") {
      setBrowseListInputValue(inputValue);
    }
  }, []);

  const handleBrowseListCreate = useCallback((inputValue) => {
    commitNewBrowseList(inputValue);
  }, [commitNewBrowseList]);

  const handleBrowseListBlur = useCallback(() => {
    if (browseListInputValue.trim()) {
      commitNewBrowseList(browseListInputValue);
    }
  }, [browseListInputValue, commitNewBrowseList]);

  const searchBrowseList = useCallback(async () => {
    if (!selectedBrowseListOption) {
      alert("Select a browse list first.");
      return;
    }

    const nextFormData = {
      ...initialFormData,
      browselistidfilter: selectedBrowseListOption.value === "new" ? "" : selectedBrowseListOption.value,
    };

    setFormData(nextFormData);
    setSelectedCityscopeOption(null);
    setSelectedStateOption(null);
    setSelectedHistoricalSearchOption(null);
    await runSearch(nextFormData);
  }, [selectedBrowseListOption, runSearch]);

  const deleteBrowseList = useCallback(async () => {
    if (!selectedBrowseListOption) {
      return;
    }

    const confirmed = window.confirm("Are you sure you want to clear the selected browse list?");
    if (!confirmed) {
      return;
    }

    try {
      const response = await put({
        apiName: "colin",
        path: `/browselists/${selectedBrowseListOption.value}`,
        options: {
          body: {
            properties: {},
            user,
          },
        },
      }).response;

      const result = await response.body.json();
      if (result?.result === false) {
        alert(result.error_message || "Unable to clear the browse list.");
        return;
      }

      await loadSearchLookups();
      setBrowseListsOptions((currentOptions) =>
        currentOptions.filter(
          (option) => String(option.value) !== String(selectedBrowseListOption.value)
        )
      );
      setSelectedBrowseListOption(null);
      setBrowselistid(null);
      setProperties([]);
      setFormData((currentFormData) => ({
        ...currentFormData,
        browselistidfilter: "",
      }));
      alert("Browse list cleared");
    } catch (error) {
      alert(error?.message || "Unable to clear the browse list.");
    }
  }, [selectedBrowseListOption, user, loadSearchLookups]);

  const resultsSection = useMemo(() => {
    if (isLoading) {
      return (
        <div id={createId("loading-container")} className="outer-div">
          <div className="sweet-loading inner-div">
            <PacmanLoader
              cssOverride={{ display: "block", margin: "0 auto" }}
              size={20}
              color={"#e03c31"}
              loading={isLoading}
            />
          </div>
        </div>
      );
    }

    return (
      <PropertyList
        isAuthenticated={isAuthenticated}
        isLoading={false}
        data={properties}
        controlType="table"
        updateTableState={updateTableState}
        saveBrowseList={saveBrowseList}
        browseListsOptions={browseListsOptions}
        handleBrowseListChange={handleBrowseListChange}
        handleBrowseListInputChange={handleBrowseListInputChange}
        handleBrowseListCreate={handleBrowseListCreate}
        browseListInputValue={browseListInputValue}
        deleteBrowseList={deleteBrowseList}
        selectedBrowseListOption={selectedBrowseListOption}
        searchBrowseList={searchBrowseList}
        handleBrowseListBlur={handleBrowseListBlur}
      />
    );
  }, [browseListInputValue, browseListsOptions, createId, deleteBrowseList, handleBrowseListBlur, handleBrowseListChange, handleBrowseListCreate, handleBrowseListInputChange, isAuthenticated, isLoading, properties, saveBrowseList, searchBrowseList, selectedBrowseListOption, updateTableState]);

  function renderProperties() {
    return (
      <Container id={createId("page")} fluid>
        <Row id={createId("header-row")}>
          <Col sm={12} md={12} lg={12}>
            <h6 id={createId("title")}>Cityscope Property Search</h6>
          </Col>
        </Row>
        <Row id={createId("layout-row")}>
          <Col md={3} lg={3}>
            <Form id={createId("form")} onSubmit={handleSubmit}>
              <Row id={createId("actions-row")}>
                <Col>
                  <Button type="submit" id={createId("button-search")}>
                    Search
                  </Button>
                </Col>
                <Col>
                  <Button id={createId("button-clear")} type="button" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </Col>
              </Row>
              <hr />

              <Row>
                <Col>
                  <Form.Group className="form-group">
                    <Form.Control
                      id={createId("input-property-key")}
                      name="propertyKey"
                      value={formData.propertyKey}
                      onChange={handleChange}
                      as="input"
                      type="text"
                      placeholder="Cityscope Reference starting"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg={3}>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-stnolow")}
                      name="stnolow"
                      type="input"
                      value={formData.stnolow}
                      onChange={handleChange}
                      placeholder="St No. Low"
                    />
                  </Form.Group>
                </Col>

                <Col lg={3}>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-stnohigh")}
                      name="stnohigh"
                      type="input"
                      value={formData.stnohigh}
                      onChange={handleChange}
                      placeholder="St No. High"
                    />
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-address")}
                      name="address"
                      type="input"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Street Name starting"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Select
                      inputId={createId("select-state")}
                      value={selectedStateOption}
                      onChange={handleStateChange}
                      options={stateOptions}
                      placeholder="State"
                      isClearable
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Select
                      inputId={createId("select-scope")}
                      value={selectedCityscopeOption}
                      onChange={handleCityscopeChange}
                      options={cityscopeOptions}
                      placeholder="Scope"
                      isClearable
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-address2")}
                      name="address2"
                      type="input"
                      value={formData.address2}
                      onChange={handleChange}
                      placeholder="Street Name 2 starting - use for cnr properties"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-building-name")}
                      name="buildingName"
                      type="input"
                      value={formData.buildingName}
                      onChange={handleChange}
                      placeholder="Building Name containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-ownername")}
                      name="ownername"
                      type="input"
                      value={formData.ownername}
                      onChange={handleChange}
                      placeholder="Owner Name containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-saleappendix")}
                      name="saleappendix"
                      type="input"
                      value={formData.saleappendix}
                      onChange={handleChange}
                      placeholder="Sales Appendix equals"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-tenant")}
                      name="tenant"
                      type="input"
                      value={formData.tenant}
                      onChange={handleChange}
                      placeholder="Tenant Text (including pre/postamble) containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-description")}
                      name="description"
                      type="input"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Description containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-zoning")}
                      name="zoning"
                      type="input"
                      value={formData.zoning}
                      onChange={handleChange}
                      placeholder="Zoning containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-historical")}
                      name="historical"
                      type="input"
                      value={formData.historical}
                      onChange={handleChange}
                      placeholder="Historic Listings containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-title")}
                      name="title"
                      type="input"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Title Details containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-saleshistory")}
                      name="saleshistory"
                      type="input"
                      value={formData.saleshistory}
                      onChange={handleChange}
                      placeholder="Sales History containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-valuations")}
                      name="valuations"
                      type="input"
                      value={formData.valuations}
                      onChange={handleChange}
                      placeholder="For Lease Text (including pre/postamble) containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-proposal")}
                      name="proposal"
                      type="input"
                      value={formData.proposal}
                      onChange={handleChange}
                      placeholder="Proposals Text containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg={6}>
                  <Form.Group>
                    <DatePicker
                      id={createId("date-activequeryfollowup")}
                      selected={formData.activequeryfollowup}
                      onChange={(e) => handleDateChange(e, "activequeryfollowup")}
                      dateFormat="dd/MM/yyyy"
                      className="form-control"
                      placeholderText="Query Resolve Date Before"
                      isClearable
                    />
                  </Form.Group>
                </Col>

                <Col lg={6}>
                  <Form.Group>
                    <Form.Check
                      id={createId("checkbox-perpetual")}
                      checked={formData.perpetual}
                      onChange={handleCheckBoxChange}
                      label="Exclude Perpetual"
                      type="checkbox"
                      name="perpetual"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg={6}>
                  <Form.Group>
                    <div className="customDatePickerWidth">
                      <DatePicker
                        id={createId("date-salesheetfollowup")}
                        selected={formData.salesheetfollowup}
                        onChange={(e) => handleDateChange(e, "salesheetfollowup")}
                        dateFormat="dd/MM/yyyy"
                        className="form-control"
                        placeholderText="Sale Sheet Followup Date Before"
                        isClearable
                      />
                    </div>
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group>
                    <div className="customDatePickerWidth">
                      <DatePicker
                        id={createId("date-saledateentered")}
                        selected={formData.saledateentered}
                        onChange={(e) => handleDateChange(e, "saledateentered")}
                        dateFormat="dd/MM/yyyy"
                        className="form-control"
                        placeholderText="Sale Entered Date Since"
                        isClearable
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Check
                      id={createId("checkbox-photorequired")}
                      checked={formData.photorequired}
                      onChange={handleCheckBoxChange}
                      label="Photo Required"
                      type="checkbox"
                      name="photorequired"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg={6}>
                  <Form.Group>
                    <Form.Check
                      id={createId("checkbox-hasleasingagent")}
                      checked={formData.hasleasingagent}
                      onChange={handleCheckBoxChange}
                      label="Has Leasing Agent?"
                      type="checkbox"
                      name="hasleasingagent"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg={6}>
                  <Form.Group>
                    <Form.Check
                      id={createId("checkbox-missingphoto")}
                      checked={formData.missingphoto}
                      onChange={handleCheckBoxChange}
                      label="Property Missing Photo?"
                      type="checkbox"
                      name="missingphoto"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Form.Group as={Col}>
                  <Form.Label className="text-right">Historical Searches</Form.Label>
                  <Select
                    inputId={createId("select-historical-search")}
                    value={selectedHistoricalSearchOption}
                    onChange={handleSearchChange}
                    options={historicalSearchOptions}
                    isClearable
                  />
                </Form.Group>
              </Row>
            </Form>
          </Col>

          <Col md={9} lg={9}>{resultsSection}</Col>
        </Row>
      </Container>
    );
  }

  return (
    <div id={createId("root")} className="PropertySearch">
      {isAuthenticated && user ? renderProperties() : null}
    </div>
  );
}

export default PropertySearch;
